import React, { useCallback, useEffect, useState } from 'react';

import { useFileUpload } from 'hooks/useFileUpload';

import BookingFormField from './BookingFormField/BookingFormField';
import type { BookingFormField as BookingFormFieldType, BookingFormSchema } from './types';

// import c from './_bookingForm.module.scss';
import './_bookingForm.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import i18n from 'i18n-react';

const c = {
  wrapper: 'BookingForm',
  logo: 'logo',
  body: 'body',
  required: 'required',
  grid: 'grid',
  userInfo: 'userInfo',
  buttonHolder: 'buttonHolder',
  btn: 'btn',
  submit: 'submit',
  container: 'container',
  iframe: 'iframe',
  bgIframe: 'bgIframe',
  iframeHolder: 'iframeHolder',
  closeIframe: 'closeIframe',
  closeText: 'closeText',
  contentIframe: 'contentIframe',
  subline: 'subline',
} as const;

interface BookingFormProps {
  schema: BookingFormSchema | null;
  logo?: string;
  handleChange: (_e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  handleChangeFile: (_fileUrl: string) => void;
  submitForm: React.FormEventHandler<HTMLFormElement>;

  agentEmail: string;
  agentFirstName?: string;
  agentLastName?: string;
  agentCompany?: string;
  agentCity?: string;
  agentCountry?: string;
  agentId?: string;
  postalCode?: string;
  termsUrl?: string | null;

  [key: string]: unknown;
}

const BookingForm = ({
  schema,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  logo,
  handleChange,
  handleChangeFile,
  submitForm,
  agentEmail,
  agentFirstName,
  agentLastName,
  agentCompany,
  agentCity,
  agentCountry,
  agentId,
  postalCode,
  termsUrl,
  ...formValues
}: BookingFormProps) => {
  const fileUrl = (formValues.file as string | undefined) ?? null;

  const arrivalDate = formValues.arrivalDate as string | undefined;
  const departureDate = formValues.departureDate as string | undefined;

  const [showIframe, setShowIframe] = useState(false);

  const { uploadFile, uploading } = useFileUpload();

  const openFancybox = () => {
    setShowIframe(true);
  };

  const closeIframe = () => {
    setShowIframe(false);
  };

  const onFilesSelected = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!file) return;

      const path = await uploadFile(file);
      handleChangeFile(path);
    },
    [uploadFile, handleChangeFile],
  );

  // Helper to create a synthetic change event for numeric values
  const createNumericChangeEvent = useCallback((fieldName: string, numericValue: number) => {
    const event = {
      target: {
        name: fieldName,
        value: numericValue.toString(),
      },
    } as React.ChangeEvent<HTMLInputElement>;
    return event;
  }, []);

  // Calculate totalNights and lengthOfStay when arrivalDate or departureDate changes
  useEffect(() => {
    if (!arrivalDate || !departureDate) return;

    const arrival = new Date(arrivalDate);
    const departure = new Date(departureDate);

    if (isNaN(arrival.getTime()) || isNaN(departure.getTime())) return;

    const diffTime = Math.abs(departure.getTime() - arrival.getTime());
    const calculatedNights = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Check which fields exist in schema and update them
    if (!schema?.fields) return;

    const hasTotalNightsField = schema.fields.some((field) => field.name === 'totalNights');
    const hasLengthOfStayField = schema.fields.some((field) => field.name === 'lengthOfStay');

    if (hasTotalNightsField) {
      handleChange(createNumericChangeEvent('totalNights', calculatedNights));
    }

    if (hasLengthOfStayField) {
      handleChange(createNumericChangeEvent('lengthOfStay', calculatedNights));
    }
  }, [arrivalDate, departureDate, schema?.fields, handleChange, createNumericChangeEvent]);

  const fields = schema?.fields ?? [];

  return (
    <div className={c.wrapper}>
      <div className={c.body}>
        <form method="post" id="formRewards" onSubmit={submitForm} noValidate>
          <h3>{i18n.translate('rewards.bookingForm.subTitle')}</h3>
          <hr className={c.subline} />

          <div className={clsx(c.grid, c.userInfo)}>
            {agentFirstName} {agentLastName}
            <br />
            {agentCompany}
            <br />
            {agentCity}
            {postalCode && ', '} {postalCode}
            <br />
            {agentCountry}
            <br />
            {agentId}
            <br />
            <br />
            <a onClick={openFancybox}>{'Edit your info >'}</a>
          </div>

          <hr />
          <br />

          <h4>{i18n.translate('rewards.bookingForm.sub2')}</h4>
          <p className={c.required}>{i18n.translate('rewards.bookingForm.required')}</p>

          <div className={c.grid}>
            {fields.map((field: BookingFormFieldType, idx: number) => {
              const value = formValues[field.name] as string | undefined;
              return (
                <React.Fragment key={field.name}>
                  {field.newLine && <div key={`newLine-${idx}`} style={{ gridColumn: '1 / -1', height: 0 }} />}
                  <BookingFormField
                    field={field}
                    value={value}
                    onChange={handleChange}
                    arrivalDate={arrivalDate}
                    departureDate={departureDate}
                    fileUrl={fileUrl}
                    uploading={uploading}
                    onFilesSelected={(files) => {
                      void onFilesSelected(files);
                    }}
                  />
                </React.Fragment>
              );
            })}
          </div>

          <br />
          <hr />

          <div className={c.buttonHolder}>
            <button type="submit" className={clsx(c.btn, c.submit)}>
              {i18n.translate('rewards.bookingForm.button')}
            </button>

            {termsUrl && (
              <a href={termsUrl} className="linkTerms" data-fancybox="terms" data-type="iframe">
                {i18n.translate('rewards.TermsAndConditions.title')}
              </a>
            )}
          </div>
        </form>
      </div>

      {showIframe && (
        <div className={c.iframe}>
          <div className={c.bgIframe} onClick={closeIframe} />
          <div className={c.iframeHolder}>
            <div className={c.closeIframe} onClick={closeIframe}>
              <span className={c.closeText}>
                <FontAwesomeIcon icon={['fas', 'chevron-left']} /> {i18n.translate('general.back')}
              </span>
            </div>

            <div className={c.contentIframe}>
              <iframe
                width="100%"
                height="100%"
                id="iframeModal"
                src={`https://accounts.travpromobile.com/profile/?header=false&email=${agentEmail}&app_id=${import.meta.env.VITE_APP_ID}`}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingForm;
